/**
 * Renders STC or MICS form data + signature as a single PNG image (for download/print).
 * Mirrors the layout from the legacy stc/index.html renderStcPNG/renderMicsPNG.
 */

const W = 1200
const H = 900
const FONT = 'system-ui, -apple-system, Segoe UI, Roboto'

const STC_TYPE_LABELS: Record<string, string> = {
  '0': '',
  C: 'C - EC RESIDENT',
  E: 'E - EMERGENCY',
  H: 'H - OUT OF HOURS',
  O: 'O - SECOND MED. OPINION',
  T: 'T - TEMPORARY RESIDENT',
}

const VISIT_CLASS_LABELS: Record<string, string> = {
  '0': '',
  A: 'A - Additional Fee',
  D: 'D - Normal Hours',
  L: 'L - Outside normal hours other than N',
  N: 'N - Midnight to 8 a.m.',
}

const VISIT_CLASS_N_OUT_OF_HOURS = 'N - 5 p.m. to 9 a.m.'

function getVisitClassLabel(visitClass: string | undefined, stcType: string | undefined): string {
  if (!visitClass) return '-'
  if (visitClass === 'N') {
    return stcType === 'H' ? VISIT_CLASS_N_OUT_OF_HOURS : VISIT_CLASS_LABELS.N
  }
  return VISIT_CLASS_LABELS[visitClass] ?? visitClass
}

const SPECIAL_SERVICE_LABELS: Record<string, string> = {
  '0': '',
  A: 'A - Excisions/Cryotherapy/Diathermy of Skin Lesions',
  AB: "AB - LARC - counselling, insertion '&' monitoring from certified GP",
  AC: 'AC - Removal of a LARC Device',
  AD: "AD - 24Hr ABPM Diagnosis '&' treatment for Individual patient care '&' not general/routine test",
  AL: 'AL - Therapeutic Phlebotomy for Haemochromatosis',
  B: 'B - Suturing Cuts & Lacerations-includes tissue glue does not include paper sutures',
  CE: 'CE - GP on sick leave due to Covid 19',
  CF: 'CF - Contraception Consultation for the purpose of accessing relevant products',
  CG: 'CG - LARC IMPLANT Fitting',
  CH: 'CH - LARC COIL Fitting',
  CI: 'CI - LARC IMPLANT Removal',
  CJ: 'CJ - LARC COIL Removal',
  CK: 'CK - Follow up consultation post LARC fitting',
  CL: 'CL - Contraception Consultation for the purpose of accessing relevant products Agreement 2023',
  CM: 'CM - LARC COIL Fitting Agreement 2023',
  CN: 'CN - LARC COIL Removal Agreement 2023',
  CO: 'CO - LARC IMPLANT Fitting Agreement 2023',
  CQ: 'CQ - LARC IMPLANT Removal Agreement 2023',
  D: 'D - Treatment and plugging of dental and nasal haemorrhages',
  F: "F - ECG tests '&' interpretation-individual patient care '&' not general/routine test",
  H: 'H - Removal of adherent foreign bodies from the surface of the Eye',
  J: 'J - Removal of lodged foreign bodies from the Ear, Nose and Throat',
  K: 'K - Nebuliser Treatment (in the case of Acute Asthmatic Attack)',
  L: 'L - Bladder catheterization – composite fee for insertion and removal',
  M: 'M - Attendance by GP at HSE convened case conference',
  X: 'X - Removal of lodged or impacted foreign bodies from the ear, nose, throat and skin',
  Y: 'Y - Suturing of cuts and lacerations (including application of tissue glue)',
  Z: 'Z - Draining of Abscess',
}

const CLAIM_TYPE_LABELS: Record<string, string> = {
  '0': 'Please Select',
  A: 'A - Capitation',
  D: 'D - FPI Surgery',
  E: 'E - FPI Domiciliary',
}

const LOCATION_LABELS: Record<string, string> = {
  '0': 'Please Select',
  D: 'D - Domiciliary',
  S: 'S - Surgery',
}

const DISTANCE_LABELS: Record<string, string> = {
  '0': 'Please Select',
  A: 'A - up to 3 miles',
  B: 'B - 3 - 5 miles',
  C: 'C - 5 - 7 miles',
  D: 'D - 7 - 10 miles',
  E: 'E - over 10 miles',
}

export interface StcFormDataForPng {
  formType: 'STC' | 'MICS'
  patientName?: string
  stcType?: string
  claimType?: string
  location?: string
  distance?: string
  date?: string
  visitClass?: string
  specialService?: string
  representative?: string
  ehicNoPrescriptionRequired?: boolean
  ehicPrescriptionSerialNumber?: string
  ehicCountryCde?: string
  ehicPatientForeName?: string
  ehicPatientSurname?: string
  ehicDob?: string
  ehicNumber?: string
  ehicPersonalIdNumber?: string
  ehicInstitutionNumber?: string
  ehicPatientExpiryDate?: string
  motherName?: string
  micsClaimDate?: string
  micsEDD?: string
  micsService?: string
  sickCertIcd10PrimaryCode?: string
  sickCertIcd10Description?: string
}

function getDateOnly(val: string | undefined): string {
  if (!val) return ''
  return val.includes('T') ? val.split('T')[0]! : val
}

function drawStcForm(
  ctx: CanvasRenderingContext2D,
  data: StcFormDataForPng,
  signatureImage: HTMLImageElement | HTMLCanvasElement
): void {
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, W, H)
  ctx.fillStyle = '#111827'
  ctx.font = `700 28px ${FONT}`
  ctx.fillText('STC Patient Declaration (PCRS)', 40, 60)
  ctx.font = `400 16px ${FONT}`
  ctx.fillStyle = '#6b7280'
  ctx.fillText('Evidence of attendance for Special Type Consultation claim', 40, 90)

  const boxY = 120
  const isEhicResident = data.stcType === 'C'
  const hasIcd10Line = Boolean(
    (data.sickCertIcd10PrimaryCode && data.sickCertIcd10PrimaryCode.trim()) ||
      (data.sickCertIcd10Description && data.sickCertIcd10Description.trim())
  )
  const icd10ExtraH = hasIcd10Line ? 42 : 0
  const boxH = (isEhicResident ? 470 : 434) + icd10ExtraH
  const boxW = W - 80
  const isOutOfHours = data.visitClass === 'N' || data.visitClass === 'L'
  ctx.strokeStyle = '#e5e7eb'
  ctx.lineWidth = 2
  ctx.strokeRect(40, boxY, boxW, boxH)

  ctx.fillStyle = '#374151'
  ctx.font = `600 18px ${FONT}`
  ctx.fillText('Patient Name:', 60, boxY + 40)
  ctx.fillText('STC Type:', 60, boxY + 82)
  const dateLabelText = isOutOfHours ? 'Date of Consultation:' : 'Date/Time of Consultation:'
  ctx.fillText(dateLabelText, 60, boxY + 124)
  ctx.fillText('Visit Class:', 60, boxY + 166)
  ctx.fillText('Type of Claim:', 60, boxY + 208)
  ctx.fillText('Location:', 60, boxY + 250)
  ctx.fillText('Distance:', 60, boxY + 292)
  ctx.fillText('Special Service:', 60, boxY + 334)
  ctx.fillText('Representative:', 60, boxY + 376)

  ctx.font = `400 18px ${FONT}`
  ctx.fillStyle = '#111827'
  ctx.fillText(data.patientName || '-', 320, boxY + 40)
  ctx.fillText(data.stcType ? (STC_TYPE_LABELS[data.stcType] ?? data.stcType) : '-', 320, boxY + 82)
  const dateDisplay = isOutOfHours ? getDateOnly(data.date) : (data.date || '')
  ctx.fillText(dateDisplay || '-', 320, boxY + 124)
  ctx.fillText(getVisitClassLabel(data.visitClass, data.stcType), 320, boxY + 166)
  ctx.fillText(
    data.claimType ? (CLAIM_TYPE_LABELS[data.claimType] ?? data.claimType) : '-',
    320,
    boxY + 208
  )
  ctx.fillText(
    data.location ? (LOCATION_LABELS[data.location] ?? data.location) : '-',
    320,
    boxY + 250
  )
  ctx.fillText(
    data.distance ? (DISTANCE_LABELS[data.distance] ?? data.distance) : '-',
    320,
    boxY + 292
  )
  ctx.fillText(
    data.specialService ? (SPECIAL_SERVICE_LABELS[data.specialService] ?? data.specialService) : '-',
    320,
    boxY + 334
  )
  ctx.fillText(data.representative || '-', 320, boxY + 376)

  if (hasIcd10Line) {
    ctx.fillStyle = '#374151'
    ctx.font = `600 18px ${FONT}`
    ctx.fillText('Primary ICD-10 (sick cert):', 60, boxY + 418)
    ctx.font = `400 18px ${FONT}`
    ctx.fillStyle = '#111827'
    const codePart = (data.sickCertIcd10PrimaryCode || '').trim()
    const descPart = (data.sickCertIcd10Description || '').trim()
    const icdDisplay = [codePart, descPart].filter(Boolean).join(' — ') || '-'
    ctx.fillText(icdDisplay, 320, boxY + 418)
  }

  const ehicTitleY = boxY + (hasIcd10Line ? 454 : 412)
  if (isEhicResident) {
    ctx.fillStyle = '#374151'
    ctx.font = `600 16px ${FONT}`
    ctx.fillText('EHIC Details', 60, ehicTitleY)
    ctx.font = `400 14px ${FONT}`
    ctx.fillStyle = '#111827'
    const noRx = data.ehicNoPrescriptionRequired ? 'Yes' : 'No'
    const ehicSummary =
      `State: ${data.ehicCountryCde || '-'} | Given: ${data.ehicPatientForeName || '-'} | Surname: ${data.ehicPatientSurname || '-'} | ` +
      `DOB: ${data.ehicDob || '-'} | Card Expiry: ${data.ehicPatientExpiryDate || '-'}`
    const ehicSummary2 =
      `No Rx Required: ${noRx} | Rx Serial: ${data.ehicPrescriptionSerialNumber || '-'} | ID: ${data.ehicNumber || '-'} | ` +
      `Personal ID: ${data.ehicPersonalIdNumber || '-'} | Institution ID: ${data.ehicInstitutionNumber || '-'}`
    ctx.fillText(ehicSummary, 60, ehicTitleY + 24)
    ctx.fillText(ehicSummary2, 60, ehicTitleY + 46)
  }

  const sigBoxY = boxY + boxH + 30
  const sigBoxH = isEhicResident ? 240 : 300
  ctx.strokeStyle = '#e5e7eb'
  ctx.strokeRect(40, sigBoxY, boxW, sigBoxH)
  ctx.fillStyle = '#374151'
  ctx.font = `400 16px ${FONT}`
  ctx.fillText('I confirm that I attended the consultation as outlined above.', 60, sigBoxY + 40)

  const sigDstW = boxW - 40
  const sigDstH = 200
  const sigDstX = 60
  const sigDstY = sigBoxY + 60
  const sw =
    signatureImage instanceof HTMLCanvasElement
      ? signatureImage.width
      : (signatureImage as HTMLImageElement).naturalWidth
  const sh =
    signatureImage instanceof HTMLCanvasElement
      ? signatureImage.height
      : (signatureImage as HTMLImageElement).naturalHeight
  const aspect = sw / sh
  let drawW = sigDstW
  let drawH = drawW / aspect
  if (drawH > sigDstH) {
    drawH = sigDstH
    drawW = drawH * aspect
  }
  const offsetX = sigDstX + (sigDstW - drawW) / 2
  const offsetY = sigDstY + (sigDstH - drawH) / 2
  ctx.drawImage(signatureImage, offsetX, offsetY, drawW, drawH)

  ctx.fillStyle = '#6b7280'
  ctx.font = `400 14px ${FONT}`
  const footerTimestamp = isOutOfHours
    ? new Date().toLocaleDateString()
    : new Date().toLocaleString()
  ctx.fillText(`Generated ${footerTimestamp} — Keep for 6+ years (PCRS audit)`, 40, H - 30)
}

function drawMicsForm(
  ctx: CanvasRenderingContext2D,
  data: StcFormDataForPng,
  signatureImage: HTMLImageElement | HTMLCanvasElement
): void {
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, W, H)
  ctx.fillStyle = '#111827'
  ctx.font = `700 28px ${FONT}`
  ctx.fillText('Mother & Infant Care Scheme (PCRS)', 40, 60)
  ctx.font = `400 16px ${FONT}`
  ctx.fillStyle = '#6b7280'
  ctx.fillText('Evidence of attendance for MICS consultation', 40, 90)

  const boxY = 120
  const boxH = 230
  const boxW = W - 80
  ctx.strokeStyle = '#e5e7eb'
  ctx.lineWidth = 2
  ctx.strokeRect(40, boxY, boxW, boxH)

  ctx.fillStyle = '#374151'
  ctx.font = `600 18px ${FONT}`
  ctx.fillText("Mother's Name:", 60, boxY + 40)
  ctx.fillText('Expected Delivery Date:', 60, boxY + 90)
  ctx.fillText('Claim Date:', 60, boxY + 140)
  ctx.fillText('Service Provided:', 60, boxY + 190)

  ctx.font = `400 18px ${FONT}`
  ctx.fillStyle = '#111827'
  ctx.fillText(data.motherName || '-', 320, boxY + 40)
  ctx.fillText(data.micsEDD || '-', 320, boxY + 90)
  ctx.fillText(data.micsClaimDate || '-', 320, boxY + 140)
  ctx.fillText(data.micsService || '-', 320, boxY + 190)

  const sigBoxY = boxY + boxH + 30
  const sigBoxH = 300
  ctx.strokeStyle = '#e5e7eb'
  ctx.strokeRect(40, sigBoxY, boxW, sigBoxH)
  ctx.fillStyle = '#374151'
  ctx.font = `400 16px ${FONT}`
  ctx.fillText(
    'I confirm attendance for the Mother & Infant Care Scheme consultation as outlined.',
    60,
    sigBoxY + 40
  )

  const sigDstW = boxW - 40
  const sigDstH = 200
  const sigDstX = 60
  const sigDstY = sigBoxY + 60
  const sw =
    signatureImage instanceof HTMLCanvasElement
      ? signatureImage.width
      : (signatureImage as HTMLImageElement).naturalWidth
  const sh =
    signatureImage instanceof HTMLCanvasElement
      ? signatureImage.height
      : (signatureImage as HTMLImageElement).naturalHeight
  const aspect = sw / sh
  let drawW = sigDstW
  let drawH = drawW / aspect
  if (drawH > sigDstH) {
    drawH = sigDstH
    drawW = drawH * aspect
  }
  const offsetX = sigDstX + (sigDstW - drawW) / 2
  const offsetY = sigDstY + (sigDstH - drawH) / 2
  ctx.drawImage(signatureImage, offsetX, offsetY, drawW, drawH)

  ctx.fillStyle = '#6b7280'
  ctx.font = `400 14px ${FONT}`
  ctx.fillText(`Generated ${new Date().toLocaleString()} — Keep for 6+ years (PCRS audit)`, 40, H - 30)
}

/**
 * Renders the full form (fields + signature) as a PNG data URL.
 * @param data Form field values (STC or MICS)
 * @param signatureDataUrl Data URL of the signature image (e.g. from canvas.toDataURL('image/png'))
 */
export function renderStcFormPng(
  data: StcFormDataForPng,
  signatureDataUrl: string
): Promise<string> {
  return new Promise((resolve, reject) => {
    const cnv = document.createElement('canvas')
    cnv.width = W
    cnv.height = H
    const ctx = cnv.getContext('2d')
    if (!ctx) {
      reject(new Error('Could not get canvas context'))
      return
    }

    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      if (data.formType === 'MICS') {
        drawMicsForm(ctx, data, img)
      } else {
        drawStcForm(ctx, data, img)
      }
      resolve(cnv.toDataURL('image/png'))
    }
    img.onerror = () => reject(new Error('Failed to load signature image'))
    img.src = signatureDataUrl
  })
}
